import { Check, ChevronsUpDown } from 'lucide-react';
import type { Key, ReactNode } from 'react';
import {
  Button as AriaButton,
  ListBox,
  ListBoxItem,
  Popover,
  Select as AriaSelect,
  SelectValue,
} from 'react-aria-components';
import { cx } from './cx';
import styles from './Field.module.css';
import { FieldHelp, FieldLabel, type FieldChromeProps } from './TextField';

export interface SelectOption {
  id: string;
  label: string;
  description?: string;
  isDisabled?: boolean;
}

export interface SelectFieldProps extends FieldChromeProps {
  options: SelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  isDisabled?: boolean;
  className?: string;
  'aria-label'?: string;
  renderOption?: (option: SelectOption) => ReactNode;
}

/** Single choice from a short, known list (React Aria Select: keyboard, typeahead, RTL). */
export function SelectField({
  label,
  description,
  errorMessage,
  isRequired,
  isOptional,
  fieldName,
  options,
  value,
  onChange,
  placeholder,
  isDisabled,
  className,
  renderOption,
  ...props
}: SelectFieldProps) {
  return (
    <AriaSelect
      {...props}
      value={value}
      onChange={(key: Key | null) => onChange(key === null ? null : String(key))}
      placeholder={placeholder}
      isDisabled={isDisabled ?? false}
      isInvalid={Boolean(errorMessage)}
      isRequired={isRequired ?? false}
      validationBehavior="aria"
      className={cx(styles.field, className)}
      data-field={fieldName}
    >
      <FieldLabel label={label} isRequired={isRequired ?? false} isOptional={isOptional ?? false} />
      <AriaButton className={styles.selectButton}>
        <SelectValue className={styles.selectValue} />
        <ChevronsUpDown size={16} aria-hidden="true" />
      </AriaButton>
      <FieldHelp description={description} errorMessage={errorMessage} />
      <Popover className={styles.popover} offset={4}>
        <ListBox className={styles.listBox} items={options}>
          {(option) => (
            <ListBoxItem
              id={option.id}
              textValue={option.label}
              isDisabled={option.isDisabled ?? false}
              className={styles.option}
            >
              {({ isSelected }) => (
                <>
                  <span className={styles.optionBody}>
                    {renderOption ? (
                      renderOption(option)
                    ) : (
                      <>
                        <span className={styles.optionTitle}>{option.label}</span>
                        {option.description ? (
                          <span className={styles.optionMeta}>{option.description}</span>
                        ) : null}
                      </>
                    )}
                  </span>
                  {isSelected ? (
                    <Check className={styles.optionCheck} size={16} aria-hidden="true" />
                  ) : null}
                </>
              )}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
}
